import { useEffect, useState, type ReactNode } from 'react';
import { get, type Ingest, type StreamKey, type StreamKeyWithSecret } from '../api';
import { srtOffer, type ObsProtocol } from './srt';
import { t } from '../i18n';
import { Icon } from '../ui/Icon';

export function Copy({ value, onCopy, testId, label = 'Copy' }: { value?: string; onCopy?: () => Promise<string | undefined>; testId?: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" data-testid={testId}
      onClick={async () => {
        const v = value ?? (await onCopy?.());
        if (!v) return;
        await navigator.clipboard?.writeText(v).catch(() => undefined);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className={`${copied ? 'done' : 'copy'} flex-none max-md:min-h-[44px]`}>
      <Icon name={copied ? 'check' : 'copy'} size={16} />{copied ? 'Copied' : label}
    </button>
  );
}

/**
 * A labelled value with its buttons: the system's IngestFields (.sx-field > .sx-combo) — mono value,
 * Show / Hide in text-soft, Copy in accent (Copied: check in live for 1.5 s). The box grows when a
 * revealed SRT address wraps.
 */
export function Row({ label, help, children }: { label: string; help?: ReactNode; children: ReactNode }) {
  return (
    <div className="sx-field">
      <span className="text-xs text-muted">{label}</span>
      <div className="sx-combo h-auto min-h-[var(--size-control-md)]">{children}</div>
      {help && <span className="help">{help}</span>}
    </div>
  );
}

/**
 * The addresses on screen are a node group's (GROUP-2c): one name over several servers. The
 * user is told it is one address that survives a server, never which server (owner, NG-04).
 */
export function groupIngest(ingest?: Ingest): boolean {
  if (!ingest) return false;
  return !!ingest.regions.find((r) => r.region === ingest.recommended_region)?.group;
}

/** The value inside a Row (.sx-combo .v). */
const val = 'v !block self-center !text-xs';

/**
 * Plain RTMP is not a way of its own (owner's decision 16.10): a footnote under RTMPS that
 * opens the rtmp address of /api/ingest with Copy, and says the key travels unencrypted.
 */
function RtmpFootnote({ rtmp }: { rtmp: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="setup-rtmp-fallback" className="-mt-1.5 flex flex-col gap-1.5">
      <button type="button" data-testid="rtmp-footnote" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        className="min-h-6 self-start text-left text-[11px] text-muted underline-offset-2 hover:text-text hover:underline max-md:min-h-[40px]">
        <span className="inline-flex items-center gap-1">{t('rtmp.footnote')} <Icon name="chevron-down" size={12} className={open ? 'rotate-180' : ''} /></span>
      </button>
      {open && (
        <>
          <Row label={t('rtmp.server')}>
            <code aria-label="RTMP server" data-testid="rtmp-address" className={`${val} truncate`}>{rtmp}</code>
            <Copy value={rtmp} testId="rtmp-copy" />
          </Row>
          <span className="flex items-center gap-1 text-[11px] text-warn"><Icon name="state-warn" size={12} />{t('rtmp.plain')}</span>
        </>
      )}
    </div>
  );
}

/** the chosen protocol: a selection, so accent with its edge (02 «selection is shape-coded», AUDIT WZ-02) */
const SEL = 'border-accent/60 bg-accent/10 text-text shadow-[inset_0_-2px_0_var(--accent)]';

const SRT_NOTE = 'SRT holds up better on networks that lose packets (Wi-Fi, mobile). Leave Stream Key in OBS empty for SRT.';

const showBtn = 'flex-none max-md:min-h-[44px] disabled:cursor-not-allowed disabled:text-muted';

/**
 * What to paste into OBS, for the chosen protocol: RTMPS (server + stream key)
 * or SRT (one address, Stream Key left empty).
 *
 * Secrets are fetched with reveal only on Show / Copy, outside TanStack Query.
 * The SRT address with its passphrase lives in this component's state only while
 * it is shown: Hide, another protocol, another address (region, key) or closing
 * the panel drop it; Copy while hidden copies it without keeping it.
 * epoch: bump to forget every secret (a new key was issued).
 */
export function ObsConnection({ ingest, streamKey, proto, onProto, epoch = 0 }: {
  ingest?: Ingest;
  streamKey?: StreamKey;
  proto: ObsProtocol;
  onProto: (p: ObsProtocol) => void;
  epoch?: number;
}) {
  const [secret, setSecret] = useState<string | undefined>();
  const [shown, setShown] = useState(false);
  const [srtUrl, setSrtUrl] = useState<string | undefined>();
  const [err, setErr] = useState('');

  const offer = srtOffer(ingest, streamKey);
  const srt = proto === 'srt' && !!offer.address;
  const server = ingest?.rtmps ?? ingest?.rtmp;

  // Forget the full SRT address whenever it is no longer what is on screen.
  useEffect(() => { setSrtUrl(undefined); }, [srt, offer.address, streamKey?.id, epoch]);
  useEffect(() => { setSecret(undefined); setShown(false); }, [streamKey?.id, epoch]);
  useEffect(() => { setErr(''); }, [srt]);

  const reveal = async (): Promise<StreamKeyWithSecret | undefined> => {
    if (!streamKey) return undefined;
    setErr('');
    try {
      return await get<StreamKeyWithSecret>(`/api/stream-keys/${streamKey.id}/reveal`);
    } catch (e) {
      setErr((e as Error).message);
      return undefined;
    }
  };
  const loadKey = async () => {
    if (secret) return secret;
    const k = await reveal();
    if (k) setSecret(k.key);
    return k?.key;
  };
  const loadSrt = async () => {
    if (srtUrl) return srtUrl;
    const k = await reveal();
    if (k && !k.srt_url) setErr("The server didn't return an SRT address for this key. Use RTMPS for now.");
    return k?.srt_url;
  };

  const options: { id: ObsProtocol; label: string; sub: string; off?: string }[] = [
    { id: 'rtmps', label: ingest && !ingest.rtmps ? 'RTMP' : 'RTMPS', sub: 'server + stream key' },
    { id: 'srt', label: 'SRT', sub: 'one address', off: ingest ? offer.why : undefined },
  ];
  const current: ObsProtocol = srt ? 'srt' : 'rtmps';
  // The note under the protocols for every region choice (Auto and each region).
  const notes = ingest
    ? [...new Set([null, ...ingest.regions.map((r) => r.region)].map((region) => srtOffer({ ...ingest, region }, streamKey).why ?? SRT_NOTE))]
    : [];
  // Secrets only in the protocol on screen: the other panel stays in the DOM (it keeps the height).
  const srtShown = srt ? srtUrl : undefined;
  const keyShown = !srt && shown && secret ? secret : undefined;

  return (
    <>
      <div data-testid="obs-protocol" data-protocol={current} className="flex flex-col gap-1.5">
        <span id="obs-protocol-label" className="text-xs text-muted">Protocol</span>
        <div role="radiogroup" aria-labelledby="obs-protocol-label" className="grid grid-cols-2 gap-1.5">
          {options.map((o) => {
            const sel = o.id === current;
            return (
              <button key={o.id} type="button" role="radio" aria-checked={sel} aria-disabled={o.off ? true : undefined}
                aria-describedby={o.off ? 'obs-srt-unavailable' : undefined}
                data-testid={`obs-protocol-${o.id}`}
                title={o.off}
                onClick={() => { if (!o.off) onProto(o.id); }}
                className={`flex min-h-[44px] flex-col justify-center rounded-md border px-2.5 text-left leading-tight md:min-h-[38px] ${
                  o.off ? 'cursor-not-allowed border-line text-muted' : sel ? SEL : 'border-line-2 text-text-soft hover:text-text'
                }`}>
                <span className={`font-mono text-xs font-medium ${o.off ? 'line-through' : ''}`}>{o.label}</span>
                <span className="text-[11px] text-muted">{o.off ? 'not available here' : o.sub}</span>
              </button>
            );
          })}
        </div>
        {/* The note's text depends on the region (switched right above): the cell keeps the
            height of the longest note any region choice would show, so a switch does not move
            what is below. The others lie invisible in the same cell. */}
        <div className="grid grid-cols-[minmax(0,1fr)] text-[11px] text-muted [&>*]:col-start-1 [&>*]:row-start-1">
          {ingest && offer.why ? (
            <div id="obs-srt-unavailable" data-testid="obs-srt-unavailable">{offer.why}</div>
          ) : (
            <div>{SRT_NOTE}</div>
          )}
          {notes.filter((n) => n !== (ingest && offer.why ? offer.why : SRT_NOTE)).map((n) => (
            <div key={n} aria-hidden="true" className="invisible">{n}</div>
          ))}
        </div>
      </div>

      {/* Both protocols' fields share one cell: the box keeps the height of the taller one,
          so switching RTMPS ⇄ SRT does not move what is below (owner, 18.10). The other one is
          invisible, inert and out of the pointer's way (it lies on top in the same cell), and
          shows no secret: a revealed key or SRT address is drawn only in the protocol on screen.
          The one track is minmax(0, 1fr) and the panels min-w-0: the long hidden fields must not
          widen the cell past its column (in the guide it ran under the "Try it" column, which then
          took the clicks meant for Show). */}
      <div data-testid="obs-fields" className="grid min-w-0 grid-cols-[minmax(0,1fr)] [&>*]:col-start-1 [&>*]:row-start-1 [&>*]:min-w-0">
      {offer.address && (
        <div className={`flex flex-col gap-3 ${srt ? '' : 'pointer-events-none invisible'}`} inert={!srt} aria-hidden={srt ? undefined : true} data-testid="obs-fields-srt">
          <Row label="SRT address">
            <code aria-label="SRT address" data-testid="obs-srt-address" data-revealed={srtShown ? 'true' : 'false'}
              className={`${val} ${srtShown ? 'whitespace-normal break-all py-2' : 'truncate !text-text-soft'}`}>
              {srtShown ?? offer.address}
            </code>
            <button type="button" data-testid="obs-srt-show" disabled={!streamKey}
              onClick={async () => {
                if (srtUrl) { setSrtUrl(undefined); return; }
                const u = await loadSrt();
                if (u) setSrtUrl(u);
              }}
              className={showBtn}>
              <Icon name={srtUrl ? 'eye-off' : 'eye'} size={16} />{srtUrl ? 'Hide' : 'Show'}
            </button>
            <Copy testId="obs-srt-copy" onCopy={loadSrt} />
          </Row>
          {!srtShown && (
            <div data-testid="obs-srt-hidden" className="-mt-1.5 text-[11px] text-muted">
              Passphrase hidden. The full address carries your stream key: <b className="font-medium text-text-soft">Copy</b> takes it whole, keep it private like the key.
            </div>
          )}
          <ul data-testid="obs-srt-hints" className="m-0 flex list-none flex-col gap-1 p-0 text-xs text-muted">
            <li>In OBS: Service <b className="font-medium text-text-soft">Custom</b>, paste the address into <b className="font-medium text-text-soft">Server</b>, leave <b className="font-medium text-text-soft">Stream Key</b> empty.</li>
            <li data-testid="obs-srt-latency">
              {t('srt.latency', { value: '2 s' })}
            </li>
          </ul>
        </div>
      )}
        <div className={`flex flex-col gap-3 ${srt ? 'pointer-events-none invisible' : ''}`} inert={srt} aria-hidden={srt ? true : undefined} data-testid="obs-fields-rtmp">
          <Row label="Server" help={groupIngest(ingest) ? <span data-testid="obs-group-help">{t('ingest.groupHelp')}</span> : undefined}>
            <code aria-label="Server" className={`${val} truncate`}>{server ?? '…'}</code>
            <Copy value={server} />
          </Row>
          <Row label="Stream key">
            <code aria-label="Stream key" className={`${val} truncate ${keyShown ? '' : 'dots'}`}>
              {keyShown ?? '•'.repeat(20)}
            </code>
            <button type="button" disabled={!streamKey}
              onClick={async () => { if (!shown && !(await loadKey())) return; setShown((v) => !v); }}
              className={showBtn}>
              <Icon name={shown ? 'eye-off' : 'eye'} size={16} />{shown ? 'Hide' : 'Show'}
            </button>
            <Copy onCopy={loadKey} />
          </Row>
          {ingest?.rtmps && ingest.rtmp && <RtmpFootnote rtmp={ingest.rtmp} />}
        </div>
      </div>
      {err && <div role="alert" className="flex items-center gap-1 text-xs text-fail"><Icon name="state-fail" size={12} />{err}</div>}
    </>
  );
}
